import {
  intlAssetValueFormatting,
  getRoundedPrecision,
  PrecisionFloat,
  intlFiatValueFormatting,
  type TimeScalar,
} from '@bts-web/utils-formatting';
import { convertToLocaleDateString } from '@bts-web/utils-formatting-client';
import { Locale } from '@bts-web/utils-lokalise';
import {
  isCryptoTransferTransaction,
  isDepositTransaction,
  isWithdrawalTransaction,
  transactionTypeByTypeName,
  TransactionTypes,
} from '../types/transactions';

export const getTransactionTime = ({
  locale,
  time,
}: {
  time: TimeScalar;
  locale: Locale;
}) => {
  return convertToLocaleDateString(time, locale);
};

export const formatAssetAmount = (
  assetAmount: PrecisionFloat,
  locale: Locale,
) => {
  if (!assetAmount) {
    return '';
  }

  return intlAssetValueFormatting(getRoundedPrecision(assetAmount), {
    locale,
    fractionDigits: assetAmount.precision,
  });
};

const isBuyOrSavingsPlanTransaction = (transactionType: TransactionTypes) => {
  return [
    transactionTypeByTypeName.BuyTransaction,
    transactionTypeByTypeName.BuyOrderTransaction,
    transactionTypeByTypeName.SavingsPlanTransaction,
  ].some((item) => item === transactionType);
};

const isGiveawayTransaction = (transactionType: TransactionTypes) =>
  transactionType === transactionTypeByTypeName.GiveawayTransaction;

export const getFiatAmountSign = (
  transactionType: TransactionTypes,
  isOutgoing: boolean,
): '+' | '-' | '' => {
  if (isCryptoTransferTransaction(transactionType)) {
    if (isWithdrawalTransaction(transactionType)) {
      return '-';
    }

    return isDepositTransaction(transactionType) ? '+' : '';
  }

  if (
    isBuyOrSavingsPlanTransaction(transactionType) ||
    (isGiveawayTransaction(transactionType) && isOutgoing)
  ) {
    return '-';
  }

  return '+';
};

export const formatFiatAmount = (
  fiatAmount: PrecisionFloat,
  sign: '+' | '-' | '',
  opts: {
    locale: Locale;
    currency: string;
  },
) => {
  return `${sign}${intlFiatValueFormatting(getRoundedPrecision(fiatAmount), {
    currency: opts.currency,
    locale: opts.locale,
  })}`;
};

export const getAssetAmountText = (
  assetAmount: string,
  amountLabel: string,
  assetType: string,
  assetSymbol: string | undefined,
): string => {
  // If expected required data is not provided, return a blank string
  const isMetal = assetType === 'MetalAsset';

  const hasSymbolOrLabel = isMetal ? amountLabel : assetSymbol;

  if (!assetAmount || !hasSymbolOrLabel) {
    return '';
  }

  return isMetal
    ? `${assetAmount} ${amountLabel}`
    : `${assetAmount} ${assetSymbol}`;
};
